import { App as AntdApp } from 'antd';
import { createElement, useMemo } from 'react';

import { ApiError, NetworkError } from './problem';
import { problemHeading, problemTone, type ProblemTone } from './problemDisplay';

/**
 * The one way anything in the app reports that something *happened*.
 *
 * Every event lands in the same corner wearing the same vocabulary, because the alternative — each
 * screen choosing its own placement, icon and colour — is how the same kind of failure comes to
 * look like three different things depending on where you are standing.
 *
 * Two rules are worth stating, because they are what this module exists to enforce:
 *
 * **An explanation is not an event.** "This role cannot discount" is always true of that role; it
 * has no moment of occurrence, so as a toast it would fire on render and then vanish, taking the
 * explanation with it. Those stay inline as a `Notice`. What belongs here is only things that just
 * happened: a save, a failure, a copy.
 *
 * **`error` takes the thrown value, not a string.** Handed a message, a caller would have to decide
 * the tone itself, and sooner or later one of them paints a plan limit red — which tells a customer
 * their software is broken when in fact they have simply reached the edge of what they bought.
 * Passing the error keeps that judgement in {@link problemTone}, next to the API's own ordering.
 */

/** Toasts come from the App context, never antd's static exports — see the note in AppProviders. */
type NotificationApi = ReturnType<typeof AntdApp.useApp>['notification'];

/**
 * The template's "Alerts with Icons" set. One glyph per tone, so no call site picks an icon.
 *
 * These are Lucide names from the vendored `icon icon-*` family, matching the template's own alert
 * examples. The colour class rides along because antd would otherwise draw the icon in its own
 * palette rather than the theme's.
 */
const GLYPHS: Record<ProblemTone | 'success', string> = {
  success: 'icon-badge-check text-success',
  info: 'icon-info text-info',
  warning: 'icon-triangle-alert text-warning',
  danger: 'icon-octagon-alert text-danger',
};

/**
 * Long enough to read a failure and decide what to do about it.
 *
 * A confirmation repeats something already visible on the page behind it, so it can leave quickly.
 * A failure is frequently the only account of something that did not happen — the drawer has often
 * closed over it — so it outlasts a glance somewhere else on the screen.
 */
const FAILURE_DURATION = 8;

export interface Notifier {
  success: (text: string) => void;
  info: (text: string) => void;
  warning: (text: string) => void;
  /** Takes the thrown value so the tone is derived, never guessed. */
  error: (error: unknown) => void;
}

export function useNotify(): Notifier {
  const { notification } = AntdApp.useApp();

  // Stable across renders so a caller can safely put it in an effect's dependencies.
  return useMemo(() => build(notification), [notification]);
}

function build(notification: NotificationApi): Notifier {
  const show = (
    tone: ProblemTone | 'success',
    message: string,
    options: { description?: string; duration?: number } = {},
  ) => {
    notification.open({
      message,
      // The server's own wording, when there is any. Never replaced by text invented here: the API
      // resolves `detail` from its own resource files and orders competing failures
      // most-fixable-first, and a generic "Something went wrong" throws that away.
      ...(options.description === undefined ? {} : { description: options.description }),

      // `icon` rather than antd's built-in `type`, which would draw its own glyph in its own colour
      // and leave the toast looking like Ant Design instead of the template.
      icon: createElement('i', { className: `icon ${GLYPHS[tone]}` }),
      className: `notify notify-${tone}`,
      ...(options.duration === undefined ? {} : { duration: options.duration }),
    });
  };

  return {
    success: (text) => show('success', text),
    info: (text) => show('info', text),
    warning: (text) => show('warning', text),

    error: (error) => {
      if (error instanceof ApiError) {
        // Heading names the audience, body is the server's `detail` — the same split the inline
        // `ApiErrorAlert` makes, from the same two functions, so the two can never disagree.
        show(problemTone(error), problemHeading(error), {
          description: error.message,
          duration: FAILURE_DURATION,
        });

        return;
      }

      if (error instanceof NetworkError) {
        show('warning', 'Cannot reach the server', {
          description: 'Check your connection and try again.',
          duration: FAILURE_DURATION,
        });

        return;
      }

      show('danger', 'Something went wrong', {
        description: error instanceof Error ? error.message : String(error),
        duration: FAILURE_DURATION,
      });
    },
  };
}
